/**
 * Compact license plate form expected by /forms/validate and order APIs:
 * no whitespace, uppercase, compatibility-normalized (NFKC).
 */
export function normalizeLicensePlateForApi(raw) {
    return String(raw ?? '')
        .normalize('NFKC')
        .replace(/[\u00A0\u2000-\u200B\uFEFF]/g, '')
        .replace(/\s+/g, '')
        .toUpperCase();
}

/**
 * Plate typed on the homepage (query string or cart). Keeps spaces the visitor
 * entered, unlike the API normalizer which strips them at submit time.
 */
export function licensePlateFromQuery(raw) {
    return String(raw ?? '')
        .normalize('NFKC')
        .replace(/[\u00A0\u2000-\u200B\uFEFF]/g, ' ')
        .replace(/[^A-Za-z0-9ÄÖÜäöü\-\s]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .toUpperCase();
}

/** Carry homepage cart fields onto the buy-vignette URL. */
export function withCartQuery(url, fields = {}) {
    const base = String(url || '').trim() || '/buy-vignette';
    let parsed;
    try {
        const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
        parsed = new URL(base, origin);
    } catch (e) {
        return base;
    }

    const set = (key, value) => {
        if (value == null) return;
        const v = String(value).trim();
        if (!v) return;
        parsed.searchParams.set(key, v);
    };

    set('category_id', fields.category_id);
    set('plate_country', fields.plate_country);
    set('license_plate', fields.license_plate);
    set('product_id', fields.product_id);
    set('start_date', fields.start_date);

    if (/^https?:\/\//i.test(base)) {
        return parsed.toString();
    }

    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

/**
 * Homepage → buy page. Prefer the live URL so a full navigation is not missed
 * when the router query is still empty.
 */
export function readCartQuery(routeQuery) {
    const params = typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search)
        : new URLSearchParams();

    const pick = (key) => {
        const fromRoute = routeQuery && routeQuery[key];
        const routeValue = Array.isArray(fromRoute) ? fromRoute[0] : fromRoute;
        if (routeValue != null && String(routeValue).trim() !== '') {
            return String(routeValue);
        }
        return params.get(key) || '';
    };

    return {
        license_plate: pick('license_plate'),
        plate_country: pick('plate_country'),
        category_id: pick('category_id'),
        product_id: pick('product_id'),
        start_date: pick('start_date'),
    };
}

export function normalizeLicensePlatesInForm(form) {
    if (!form || typeof form !== 'object') return form;

    if (form.license_plate != null && String(form.license_plate).trim() !== '') {
        form.license_plate = normalizeLicensePlateForApi(form.license_plate);
    }
    if (form.license_plate_confirmation != null && String(form.license_plate_confirmation).trim() !== '') {
        form.license_plate_confirmation = normalizeLicensePlateForApi(form.license_plate_confirmation);
    }

    return form;
}
